// =====================================================================
// Bank details — reusable popup + shared field config.
//
// The 9 bank columns already live on the employee record (accountNum,
// accountType, ifscCode, bankName, bankbranch, swiftCode, upiCode, qrcodeUrl,
// othersBankDetails) and are persisted through the existing
// PUT /update_employee/{id} endpoint, so this is a pure-frontend feature.
//
// Used in two places:
//   • Payroll & Tax Summary (self-service) — this modal lets a user edit
//     and save their own bank details.
//   • Add / Edit Employee form (admin) — reuses BANK_FIELDS to render the
//     same fields inline as part of the employee form (saved on submit).
//
// Because both sides read/write the same employee record, whatever the
// admin enters shows up on the user's side and vice-versa.
// =====================================================================

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Landmark, X, Save, Loader2, Paperclip, Upload } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "../hooks/useToast";
import { getEmployeeById, updateEmployee, uploadBankAttachment } from "../api/employees";
import type { BankAttachment } from "../api/employees";

export type BankValues = {
  accountNum: string;
  accountType: string;
  ifscCode: string;
  bankName: string;
  bankbranch: string;
  swiftCode: string;
  upiCode: string;
  qrcodeUrl: string;
  othersBankDetails: string;
  // Optional supporting documents (cancelled cheque, passbook, statement…).
  bankAttachments: BankAttachment[];
};

export const EMPTY_BANK_VALUES: BankValues = {
  accountNum: "",
  accountType: "",
  ifscCode: "",
  bankName: "",
  bankbranch: "",
  swiftCode: "",
  upiCode: "",
  qrcodeUrl: "",
  othersBankDetails: "",
  bankAttachments: [],
};

// Options for the Account Type field — matches the values banks expect on
// bulk salary-transfer upload sheets (see export_salary_transfer_sheet).
export const ACCOUNT_TYPE_OPTIONS = ["SAVINGS", "CURRENT"] as const;

// Bank text fields only — attachments are handled separately by
// BankAttachmentsField, so they're excluded from this key union.
export type BankFieldKey = Exclude<keyof BankValues, "bankAttachments">;

// Single source of truth for the bank fields — shared by this modal and the
// inline section in the employee form so the two never drift apart.
export const BANK_FIELDS: Array<{
  key: BankFieldKey;
  label: string;
  placeholder: string;
  required?: boolean;
  uppercase?: boolean;
  wide?: boolean;
  /** Renders as a <select> populated from `options` instead of a text input. */
  options?: readonly string[];
}> = [
  { key: "accountNum", label: "Account Number", placeholder: "Bank account number", required: true },
  { key: "accountType", label: "Account Type", placeholder: "Select account type", required: true, options: ACCOUNT_TYPE_OPTIONS },
  { key: "ifscCode", label: "IFSC Code", placeholder: "e.g. HDFC0001234", required: true, uppercase: true },
  { key: "bankName", label: "Bank Name", placeholder: "e.g. HDFC Bank", required: true },
  { key: "bankbranch", label: "Branch", placeholder: "Branch name / location" },
  { key: "swiftCode", label: "SWIFT Code", placeholder: "For international transfers", uppercase: true },
  { key: "upiCode", label: "UPI ID", placeholder: "name@bank" },
  { key: "qrcodeUrl", label: "UPI QR Code URL", placeholder: "https://…" },
  { key: "othersBankDetails", label: "Other Details", placeholder: "Anything else worth noting", wide: true },
];

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;

export const isValidIfsc = (v: string) => !v || IFSC_RE.test(v.trim().toUpperCase());

// Pull the bank fields out of an employee record (which may arrive wrapped in
// a couple of different shapes from getEmployeeById).
export function extractBankValues(emp: any): BankValues {
  const src = emp?.employee?.employee || emp?.employee || emp || {};
  return {
    accountNum: src.accountNum ?? "",
    accountType: src.accountType ?? "",
    ifscCode: src.ifscCode ?? "",
    bankName: src.bankName ?? "",
    bankbranch: src.bankbranch ?? "",
    swiftCode: src.swiftCode ?? "",
    upiCode: src.upiCode ?? "",
    qrcodeUrl: src.qrcodeUrl ?? "",
    othersBankDetails: src.othersBankDetails ?? "",
    bankAttachments: Array.isArray(src.bankAttachments) ? src.bankAttachments : [],
  };
}

// Trim + normalise before sending to the backend.
export function normaliseBankValues(values: BankValues): BankValues {
  return {
    accountNum: values.accountNum.trim(),
    accountType: values.accountType.trim().toUpperCase(),
    ifscCode: values.ifscCode.trim().toUpperCase(),
    bankName: values.bankName.trim(),
    bankbranch: values.bankbranch.trim(),
    swiftCode: values.swiftCode.trim().toUpperCase(),
    upiCode: values.upiCode.trim(),
    qrcodeUrl: values.qrcodeUrl.trim(),
    othersBankDetails: values.othersBankDetails.trim(),
    bankAttachments: values.bankAttachments || [],
  };
}

// Shared attachments editor for bank details — reused by this modal AND the
// admin employee form so the two never drift apart (same idea as BANK_FIELDS).
// Files upload to GCS immediately; `url` is an absolute public link.
export function BankAttachmentsField({
  value,
  onChange,
  disabled = false,
}: {
  value: BankAttachment[];
  onChange: (next: BankAttachment[]) => void;
  disabled?: boolean;
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const items = value || [];

  const addFiles = async (files: FileList | File[] | null) => {
    const list = files ? Array.from(files) : [];
    if (!list.length) return;
    setUploading(true);
    const added: BankAttachment[] = [];
    try {
      for (const file of list) {
        try {
          added.push(await uploadBankAttachment(file));
        } catch (err: any) {
          toast({
            title: "Couldn't upload attachment",
            description: err?.response?.data?.detail || err?.message || file.name,
            variant: "destructive",
          });
        }
      }
      if (added.length) onChange([...items, ...added]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="sm:col-span-2">
      <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        Attachments{" "}
        <span className="normal-case font-normal text-gray-400">
          (optional — cancelled cheque, passbook…)
        </span>
      </Label>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          addFiles(e.target.files);
          if (e.target) e.target.value = "";
        }}
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {items.map((att, i) => (
          <span
            key={`${att.url}-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 pl-2 pr-1 py-1 text-xs text-gray-700 max-w-[220px]"
          >
            <a
              href={att.url}
              target="_blank"
              rel="noreferrer"
              download={att.name}
              className="inline-flex items-center gap-1 min-w-0 hover:text-blue-600"
              title={att.name}
            >
              <Paperclip className="w-3 h-3 shrink-0" />
              <span className="truncate">{att.name}</span>
            </a>
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="text-gray-400 hover:text-red-500 rounded-full p-0.5"
                aria-label={`Remove ${att.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-3 py-1 text-xs text-gray-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-60"
          >
            {uploading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Upload className="w-3 h-3" />
            )}
            {uploading ? "Uploading…" : "Add file"}
          </button>
        )}
        {disabled && items.length === 0 && (
          <span className="text-xs text-gray-400">None</span>
        )}
      </div>
    </div>
  );
}

interface BankDetailsModalProps {
  open: boolean;
  onClose: () => void;
  /** Employee record id to read from / write to. */
  employeeId: string | null;
  /** Pre-fill instead of fetching (optional). */
  initialValues?: BankValues;
  /** Read-only view — hides the Save button and disables inputs. */
  readOnly?: boolean;
  /** Called with the saved values after a successful save. */
  onSaved?: (values: BankValues) => void;
  title?: string;
  subtitle?: string;
}

export default function BankDetailsModal({
  open,
  onClose,
  employeeId,
  initialValues,
  readOnly = false,
  onSaved,
  title = "Bank Details",
  subtitle,
}: BankDetailsModalProps) {
  const { toast } = useToast();
  const [values, setValues] = useState<BankValues>(EMPTY_BANK_VALUES);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load the current bank details whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    if (initialValues) {
      setValues({ ...EMPTY_BANK_VALUES, ...initialValues });
      return;
    }
    if (!employeeId) return;
    let active = true;
    setLoading(true);
    getEmployeeById(employeeId)
      .then((res: any) => {
        if (active) setValues(extractBankValues(res));
      })
      .catch((err: any) => {
        if (active)
          toast({
            title: "Couldn't load bank details",
            description: err?.message || "Please try again.",
            variant: "destructive",
          });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // toast is stable; intentionally keyed on open/employeeId only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employeeId, initialValues]);

  const ifscOk = isValidIfsc(values.ifscCode);
  const requiredOk =
    values.accountNum.trim() && values.accountType.trim() && values.ifscCode.trim() && values.bankName.trim();
  const canSave = !readOnly && !!requiredOk && ifscOk && !saving && !loading;

  const setField = (key: BankFieldKey, raw: string, uppercase?: boolean) =>
    setValues((prev) => ({ ...prev, [key]: uppercase ? raw.toUpperCase() : raw }));

  const handleSave = async () => {
    if (!employeeId || !canSave) return;
    const payload = normaliseBankValues(values);
    try {
      setSaving(true);
      await updateEmployee(employeeId, payload as any);
      toast({ title: "Bank details saved" });
      onSaved?.(payload);
      onClose();
    } catch (err: any) {
      toast({
        title: "Couldn't save bank details",
        description:
          err?.response?.data?.detail || err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Landmark className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-base font-bold text-gray-900">{title}</h3>
              <p className="text-xs text-gray-500">
                {subtitle || "Used to credit your salary. Keep it accurate."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 rounded-md p-1"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {BANK_FIELDS.map((f) => (
                <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {f.label}
                    {f.required && <span className="text-red-500 ml-0.5">*</span>}
                  </Label>
                  {f.options ? (
                    <select
                      value={values[f.key]}
                      onChange={(e) => setField(f.key, e.target.value, f.uppercase)}
                      disabled={readOnly}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm mt-1.5"
                    >
                      <option value="">{f.placeholder}</option>
                      {f.options.map((opt) => (
                        <option key={opt} value={opt}>{opt.charAt(0) + opt.slice(1).toLowerCase()}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={values[f.key]}
                      onChange={(e) => setField(f.key, e.target.value, f.uppercase)}
                      placeholder={f.placeholder}
                      disabled={readOnly}
                      className="mt-1.5"
                    />
                  )}
                  {f.key === "ifscCode" && values.ifscCode && !ifscOk && (
                    <p className="text-xs text-red-500 mt-1">
                      Enter a valid 11-character IFSC (e.g. HDFC0001234).
                    </p>
                  )}
                </div>
              ))}
              <BankAttachmentsField
                value={values.bankAttachments}
                onChange={(next) =>
                  setValues((prev) => ({ ...prev, bankAttachments: next }))
                }
                disabled={readOnly}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex justify-end gap-2 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button
              onClick={handleSave}
              disabled={!canSave}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
